/* PACE — motion engine. Vanilla JS, no deps. Ports the behaviour specced in
   design_handoff_pace_store/README.md (curtain, cursor, hero/story/gallery/drop scroll math,
   accordions, predictive search, cart) onto real Shopify objects/endpoints. */
(() => {
  'use strict';

  const doc = document;
  const qs = (sel, root) => (root || doc).querySelector(sel);
  const qsa = (sel, root) => Array.from((root || doc).querySelectorAll(sel));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const prefersReduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function motionLevel() {
    const setting = doc.documentElement.dataset.motion || 'cinematic';
    if (prefersReduced()) return 0;
    return setting === 'off' ? 0 : setting === 'subtle' ? 0.5 : 1;
  }

  function formatMoney(cents) {
    const format = (window.PaceTheme && window.PaceTheme.moneyFormat) || 'Rs {{amount_no_decimals}}';
    const amount = Math.round(cents / 100).toLocaleString('en-US');
    return format.replace('{{amount_no_decimals}}', amount).replace('{{amount}}', amount);
  }

  /* ---------------------------------------------------------------- Toast */
  const Toast = (() => {
    let el, timer;
    function init() { el = qs('#pace-toast'); }
    function show(text) {
      if (!el) return;
      clearTimeout(timer);
      el.textContent = text;
      el.classList.add('is-visible');
      timer = setTimeout(() => el.classList.remove('is-visible'), 2600);
    }
    return { init, show };
  })();
  window.PaceToast = Toast;

  /* ---------------------------------------------------- Announcement bar */
  const AnnouncementRotator = (() => {
    function init() {
      const root = qs('#pace-announcement-bar');
      if (!root) return;
      const items = qsa('.pace-announcement-bar__item', root);
      if (items.length < 2) return;
      // Keeps rotating even under prefers-reduced-motion — the CSS swaps the
      // fade/slide transition for a near-instant one in that case rather than
      // freezing the content, since it's a text change, not a motion effect.
      const ms = parseInt(root.dataset.autoRotate, 10) || 4000;
      let i = items.findIndex((el) => el.classList.contains('is-active'));
      if (i < 0) i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, ms);
    }
    return { init };
  })();

  /* -------------------------------------------------------------- Cursor */
  const Cursor = (() => {
    let el, label, raf, pending, fine;
    function init() {
      el = qs('#pace-cursor');
      label = qs('#pace-cursor-label', el);
      fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
      if (!el || !fine) return;
      doc.addEventListener('mousemove', onMove, { passive: true });
      doc.addEventListener('mouseleave', () => { el.style.opacity = '0'; });
    }
    function onMove(e) {
      pending = { x: e.clientX, y: e.clientY, target: e.target };
      if (!raf) raf = requestAnimationFrame(apply);
    }
    function apply() {
      raf = 0;
      if (!pending || !el) return;
      const { x, y, target } = pending;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      el.style.opacity = '1';
      const active = target && target.closest ? target.closest('a,button,input,[data-cursor]') : null;
      if (active) {
        el.style.width = el.style.height = '44px';
        el.style.margin = '-22px 0 0 -22px';
        el.style.background = 'transparent';
        el.style.borderColor = 'var(--pace-paper)';
        el.style.mixBlendMode = 'difference';
        el.style.fontSize = '';
        el.style.letterSpacing = '';
        label.textContent = '';
      } else {
        el.style.width = el.style.height = '12px';
        el.style.margin = '-6px 0 0 -6px';
        el.style.background = 'var(--pace-paper)';
        el.style.borderColor = 'var(--pace-paper)';
        el.style.mixBlendMode = 'difference';
        el.style.fontSize = '';
        el.style.letterSpacing = '';
        label.textContent = '';
      }
    }
    return { init };
  })();

  /* ------------------------------------------------------------- Header
     The header-group has TWO section wrappers (announcement-bar, header),
     each independently carrying .shopify-section-group-header-group — so
     each is independently position:fixed. We offset the header wrapper by
     the announcement bar's real height (--announcement-h) so they stack
     instead of overlapping, and keep --header-h as their combined total
     for page-content offset. */
  const Header = (() => {
    function init() {
      update();
      const groups = qsa('.shopify-section-group-header-group');
      if (groups.length) {
        syncHeight(groups);
        if (window.ResizeObserver) {
          const ro = new ResizeObserver(() => syncHeight(groups));
          groups.forEach((g) => ro.observe(g));
        } else {
          window.addEventListener('resize', () => syncHeight(groups));
        }
      }
    }
    function syncHeight(groups) {
      const announcementH = groups[0] ? groups[0].offsetHeight : 0;
      const total = groups.reduce((sum, g) => sum + g.offsetHeight, 0);
      document.documentElement.style.setProperty('--announcement-h', announcementH + 'px');
      document.documentElement.style.setProperty('--header-h', total + 'px');
    }
    function update() {
      doc.body.classList.toggle('is-scrolled', window.scrollY > 24);
    }
    return { init, update };
  })();

  /* ----------------------------------------------------------- Scroll lock
     Menu/search/cart/size-guide overlays all need the page behind them to
     stop scrolling. `overflow:hidden` on body alone doesn't hold on iOS
     Safari (it still allows touch-scroll of background content), so this
     pins body with position:fixed at the current scroll offset instead —
     the standard cross-browser-safe technique — and restores it after. */
  const ScrollLock = (() => {
    let count = 0, savedY = 0;
    function lock() {
      if (count === 0) {
        savedY = window.scrollY;
        doc.body.style.top = `-${savedY}px`;
        doc.body.classList.add('pace-scroll-locked');
      }
      count++;
    }
    function unlock() {
      count = Math.max(0, count - 1);
      if (count === 0) {
        doc.body.classList.remove('pace-scroll-locked');
        doc.body.style.top = '';
        window.scrollTo(0, savedY);
      }
    }
    return { lock, unlock };
  })();

  /* --------------------------------------------------------------- Menu */
  const Menu = (() => {
    function init() {
      const menu = qs('#pace-menu');
      if (!menu) return;
      qsa('[data-menu-open]').forEach((b) => b.addEventListener('click', () => { if (menu.hidden) { menu.hidden = false; ScrollLock.lock(); } }));
      qsa('[data-menu-close]').forEach((b) => b.addEventListener('click', () => { if (!menu.hidden) { menu.hidden = true; ScrollLock.unlock(); } }));
      qsa('[data-menu-submenu-toggle]', menu).forEach((btn) => {
        const submenu = btn.closest('.pace-menu-item').querySelector('[data-menu-submenu]');
        if (!submenu) return;
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          submenu.style.maxHeight = open ? '0px' : submenu.scrollHeight + 'px';
        });
      });
    }
    return { init };
  })();

  /* --------------------------------------------------------- Size guide */
  const SizeGuide = (() => {
    function init() {
      const root = qs('#pace-size-guide');
      if (!root) return;
      qsa('[data-size-guide-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); if (!root.classList.contains('is-open')) { root.classList.add('is-open'); ScrollLock.lock(); } }));
      qsa('[data-size-guide-close]').forEach((b) => b.addEventListener('click', () => { if (root.classList.contains('is-open')) { root.classList.remove('is-open'); ScrollLock.unlock(); } }));
      doc.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.classList.contains('is-open')) { root.classList.remove('is-open'); ScrollLock.unlock(); } });
    }
    return { init };
  })();

  /* ------------------------------------------------------------ Curtain */
  const Curtain = (() => {
    let el, navigating = false, firstLoad = true;

    function init() {
      el = qs('#pace-curtain');
      if (!el) return;
      const m = motionLevel();
      if (m === 0) {
        el.classList.remove('is-cover', 'is-animating');
        doc.documentElement.classList.add('is-loaded');
        return;
      }
      if (firstLoad) {
        firstLoad = false;
        setTimeout(() => {
          el.classList.add('is-animating');
          doc.documentElement.classList.add('is-loaded');
          requestAnimationFrame(() => { el.classList.remove('is-cover'); el.classList.add('is-reveal'); });
        }, 350);
        setTimeout(() => { el.classList.remove('is-animating', 'is-reveal', 'is-cover'); }, 1150);
      } else {
        el.classList.remove('is-cover', 'is-animating');
        doc.documentElement.classList.add('is-loaded');
      }
      bindLinks();
      window.addEventListener('popstate', (e) => navigateTo(location.href, { push: false }));
    }

    function bindLinks() {
      doc.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target.closest && e.target.closest('a[href]');
        if (!a) return;
        if (a.hasAttribute('data-no-transition') || a.target === '_blank' || a.hasAttribute('download')) return;
        const url = new URL(a.href, location.href);
        if (url.origin !== location.origin) return;
        if (url.pathname.startsWith('/cart') || url.pathname.startsWith('/account') || url.pathname.startsWith('/checkout')) return;
        if (url.pathname === location.pathname && url.search === location.search && url.hash) return; // same-page anchor
        e.preventDefault();
        navigateTo(url.href, { push: true });
      });
    }

    async function navigateTo(url, opts) {
      closeOverlays();
      if (navigating || motionLevel() === 0) {
        if (motionLevel() === 0) { doSwap(url, opts); return; }
        return;
      }
      navigating = true;
      el.classList.add('is-animating', 'is-cover');
      el.classList.remove('is-reveal');
      let html = null;
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        html = await res.text();
      } catch (err) { /* fall through to hard nav */ }
      setTimeout(() => {
        if (html) applySwap(url, html, opts); else location.href = url;
      }, 760);
      setTimeout(() => { el.classList.remove('is-cover'); el.classList.add('is-reveal'); }, 820);
      setTimeout(() => { el.classList.remove('is-animating', 'is-cover', 'is-reveal'); navigating = false; }, 1650);
    }

    function closeOverlays() {
      const search = qs('#pace-search');
      if (search && search.classList.contains('is-open')) { search.classList.remove('is-open'); ScrollLock.unlock(); }
      const menu = qs('#pace-menu');
      if (menu && !menu.hidden) { menu.hidden = true; ScrollLock.unlock(); }
      const sizeGuide = qs('#pace-size-guide');
      if (sizeGuide && sizeGuide.classList.contains('is-open')) { sizeGuide.classList.remove('is-open'); ScrollLock.unlock(); }
    }

    function doSwap(url, opts) {
      fetch(url, { credentials: 'same-origin' }).then((r) => r.text()).then((html) => applySwap(url, html, opts));
    }

    function applySwap(url, html, opts) {
      const doc2 = new DOMParser().parseFromString(html, 'text/html');
      const newMain = doc2.querySelector('main[data-pace-main]');
      const curMain = qs('main[data-pace-main]');
      if (newMain && curMain) {
        curMain.replaceWith(newMain);
        doc.title = doc2.title;
        window.scrollTo(0, 0);
        if (opts && opts.push) history.pushState({}, '', url);
        Pace.reinit();
      } else {
        location.href = url;
      }
    }

    return { init };
  })();

  /* --------------------------------------------------------- Hero panel */
  const Hero = (() => {
    let section;
    function init() {
      section = qs('[data-section="hero-pace"]');
      if (!section) return;
      section.addEventListener('mousemove', onMove);
      section.addEventListener('mouseleave', onLeave);
      qsa('[data-hero-swatch]', section).forEach((btn) => {
        btn.addEventListener('click', () => selectColorway(btn.dataset.heroSwatch));
      });
    }
    function selectColorway(index) {
      qsa('[data-hero-swatch]', section).forEach((b) => b.classList.toggle('is-active', b.dataset.heroSwatch === index));
      qsa('[data-hero-image]', section).forEach((img) => img.classList.toggle('is-active', img.dataset.heroImage === index));
      const active = qs(`[data-hero-swatch="${index}"]`, section);
      if (active) {
        const nameEl = qs('[data-hero-color-name]', section);
        const counterEl = qs('[data-hero-color-counter]', section);
        const glow = qs('[data-hero-glow]', section);
        if (nameEl) nameEl.textContent = active.dataset.colorName;
        if (counterEl) counterEl.textContent = active.dataset.counter;
        if (glow) glow.dataset.glowColor = active.dataset.glow;
      }
    }
    function onMove(e) {
      const r = section.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      apply(x, y, true);
    }
    function onLeave() { apply(0, 0, false); }
    function apply(x, y, hovering) {
      const m = motionLevel();
      const tilt = qs('[data-hero-tilt]', section);
      if (tilt) tilt.style.transform = `perspective(1200px) rotateY(${x * 22 * m}deg) rotateX(${-y * 16 * m}deg) translate3d(${x * 28 * m}px, ${y * 20 * m}px, 0)`;
      const imgWrap = qs('[data-hero-image-wrap]', section);
      if (imgWrap) imgWrap.style.transform = `translate(${x * -26 * m}px, ${y * -18 * m}px)`;
      const card = qs('[data-hero-card]', section);
      if (card) card.style.transform = `translate(${x * -52 * m}px, ${y * -38 * m}px) rotate(${x * -3 * m}deg)`;
      const ghost = qs('[data-hero-ghost]', section);
      if (ghost) ghost.style.transform = `translate(${x * -70 * m}px, ${y * -40 * m}px)`;
      const slash = qs('[data-hero-slash]', section);
      if (slash) slash.style.transform = `translate(${x * 40 * m}px, ${y * 50 * m}px) skewX(-18deg)`;
      const glow = qs('[data-hero-glow]', section);
      if (glow) {
        const cx = hovering ? 50 + x * 100 : 72, cy = hovering ? 50 + y * 100 : 42;
        const color = glow.dataset.glowColor || 'rgba(242,107,29,.22)';
        glow.style.background = `radial-gradient(ellipse 50% 45% at ${cx}% ${cy}%, ${color}, transparent 70%)`;
      }
    }
    return { init };
  })();

  /* ------------------------------------------------------- Scroll rig
     Story-pinned and featured-drop read scroll progress against their own
     wrapper; batched into one rAF tick. */
  const ScrollRig = (() => {
    let raf = 0;
    let galleryRO = null;
    function init() {
      initGallerySizing();
      schedule();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', () => { sizeGallery(); schedule(); });
    }
    function schedule() { if (!raf) raf = requestAnimationFrame(tick); }
    function tick() {
      raf = 0;
      Header.update();
      updateStory();
      updateGallery();
      updateDrop();
    }

    /* The pinned line-up's wrapper height must match the track's actual
       width exactly, or it either cuts the pan short or leaves dead scroll
       space. A one-shot measurement goes stale (fonts/images settling,
       theme-editor section swaps), so a ResizeObserver keeps it correct for
       the life of the element instead of guessing when to re-measure. */
    function initGallerySizing() {
      if (galleryRO) { galleryRO.disconnect(); galleryRO = null; }
      const section = qs('[data-section="gallery-horizontal"]');
      if (!section) return;
      const track = qs('[data-gallery-track]', section);
      if (!track) return;
      sizeGallery();
      if (window.ResizeObserver) {
        galleryRO = new ResizeObserver(sizeGallery);
        galleryRO.observe(track);
        galleryRO.observe(track.parentElement);
      }
    }
    function sizeGallery() {
      const section = qs('[data-section="gallery-horizontal"]');
      if (!section) return;
      if (window.matchMedia && window.matchMedia('(max-width: 860px)').matches) { section.style.height = ''; return; }
      const track = qs('[data-gallery-track]', section);
      if (!track) return;
      const parentWidth = track.parentElement.clientWidth;
      if (!parentWidth) return;
      const vh = window.innerHeight || 800;
      const max = Math.min(8000, Math.max(0, track.scrollWidth - parentWidth));
      section.style.height = (vh + max * 1.15) + 'px';
    }

    function updateStory() {
      const section = qs('[data-section="story-pinned"]');
      if (!section) return;
      if (window.matchMedia && window.matchMedia('(max-width: 860px)').matches) return;
      const vh = window.innerHeight || 800;
      const m = motionLevel();
      const r = section.getBoundingClientRect();
      const p = clamp(-r.top / Math.max(1, r.height - vh), 0, 1);
      const panel = qs('[data-story-panel]', section);
      if (panel) panel.style.transform = `translate(calc(-50% + ${(6 - 9 * p) * m}vw), calc(-50% + ${(0.5 - p) * 6 * m}vh)) rotate(${(-8 + 16 * p) * m}deg) scale(${1 - 0.08 * m + 0.2 * p * m})`;
      const rail = qs('[data-story-rail-fill]', section);
      if (rail) rail.style.width = (p * 100) + '%';
      const chapterIndex = Math.min(3, Math.floor(p * 4));
      if (section.dataset.activeChapter !== String(chapterIndex)) {
        section.dataset.activeChapter = String(chapterIndex);
        const numEl = qs('[data-story-chapter-num]', section);
        if (numEl) numEl.textContent = '0' + (chapterIndex + 1);
        qsa('[data-story-index]', section).forEach((b) => b.classList.toggle('is-active', Number(b.dataset.storyIndex) === chapterIndex));
        qsa('[data-story-photo]', section).forEach((el) => el.classList.toggle('is-active', Number(el.dataset.storyPhoto) === chapterIndex));
        qsa('[data-story-copy]', section).forEach((el) => {
          const i = Number(el.dataset.storyCopy);
          el.classList.remove('is-active', 'is-prev', 'is-next');
          el.classList.add(i === chapterIndex ? 'is-active' : i < chapterIndex ? 'is-prev' : 'is-next');
        });
      }
    }

    function updateGallery() {
      const section = qs('[data-section="gallery-horizontal"]');
      if (!section) return;
      if (window.matchMedia && window.matchMedia('(max-width: 860px)').matches) return;
      const vh = window.innerHeight || 800;
      const r = section.getBoundingClientRect();
      const p = clamp(-r.top / Math.max(1, r.height - vh), 0, 1);
      const track = qs('[data-gallery-track]', section);
      if (track) {
        const max = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
        track.style.transform = `translateX(${-p * max}px)`;
      }
      const prog = qs('[data-gallery-progress]', section);
      if (prog) prog.style.width = (p * 100) + '%';
    }

    function updateDrop() {
      const section = qs('[data-section="featured-drop"]');
      if (!section) return;
      const vh = window.innerHeight || 800;
      const m = motionLevel();
      const r = section.getBoundingClientRect();
      const p = clamp(1 - (r.top + r.height) / (vh + r.height), 0, 1);
      const img = qs('[data-drop-image]', section);
      if (img) img.style.transform = `translateY(${(p - 0.5) * -100 * m}px)`;
      const slash = qs('[data-drop-slash]', section);
      if (slash) slash.style.transform = `translateX(${(p - 0.5) * -220 * m}px) skewX(-18deg)`;
    }

    return { init, schedule, initGallerySizing };
  })();

  /* ------------------------------------------------------ Story chapter click */
  const StoryChapters = (() => {
    function init() {
      const section = qs('[data-section="story-pinned"]');
      if (!section) return;
      qsa('[data-story-index]', section).forEach((btn) => {
        btn.addEventListener('click', () => goToChapter(section, Number(btn.dataset.storyIndex)));
      });
    }
    function goToChapter(section, i) {
      const rect = section.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const vh = window.innerHeight || 800;
      const target = top + ((i + 0.5) / 4) * (section.offsetHeight - vh);
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
    return { init };
  })();

  /* --------------------------------------------------------- Grid reveal */
  const GridReveal = (() => {
    function init() {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        qsa('[data-grid-card]:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
      }));
    }
    return { init };
  })();

  /* -------------------------------------------- Horizontal row wheel-scroll
     [data-gallery-track] is a plain overflow-x row — fine for touch/trackpad,
     but a standard vertical-only mouse wheel can't move it at all. While the
     cursor is over the row, redirect vertical wheel delta into horizontal
     scroll; once the row is at its start/end, stop intercepting so the page
     keeps scrolling normally underneath. */
  const WheelScroll = (() => {
    const bound = new WeakSet();
    function init() {
      qsa('[data-gallery-track]').forEach((track) => {
        if (bound.has(track)) return;
        bound.add(track);
        track.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          const canLeft = track.scrollLeft > 0;
          const canRight = track.scrollLeft < track.scrollWidth - track.clientWidth - 1;
          if ((e.deltaY < 0 && canLeft) || (e.deltaY > 0 && canRight)) {
            e.preventDefault();
            track.scrollLeft += e.deltaY;
          }
        }, { passive: false });
      });
    }
    return { init };
  })();

  /* -------------------------------------------------------- Category gate
     Collection landing tiles vs. the filtered grid — entirely client-side
     via a plain ?type= param, since it doesn't depend on Shopify's native
     collection-filter app (filter.p.product_type) being enabled in the
     Search & Discovery app, which is store config this theme can't set. */
  const CategoryGate = (() => {
    function init() {
      const tiles = qs('[data-category-tiles]');
      const gridWrap = qs('[data-type-grid]');
      const breadcrumbExtra = qs('[data-breadcrumb-extra]');
      if (!tiles || !gridWrap) return;
      const type = new URLSearchParams(location.search).get('type');
      if (type) {
        tiles.hidden = true;
        gridWrap.hidden = false;
        applyType(gridWrap, type, breadcrumbExtra);
        const landmark = qs('#collection-landmark');
        if (landmark) {
          setTimeout(() => landmark.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
        }
      } else {
        tiles.hidden = false;
        gridWrap.hidden = true;
        if (breadcrumbExtra) breadcrumbExtra.hidden = true;
      }
    }
    function applyType(gridWrap, type, breadcrumbExtra) {
      const cards = qsa('[data-grid-card]', gridWrap);
      let count = 0;
      cards.forEach((card) => {
        const match = card.dataset.productType === type;
        card.style.display = match ? '' : 'none';
        if (match) count++;
      });
      const countEl = qs('[data-filter-count]', gridWrap);
      if (countEl) countEl.textContent = count + (count === 1 ? ' style' : ' styles');
      let activeLabel = '';
      qsa('[data-type-chip]', gridWrap).forEach((chip) => {
        const isActive = chip.dataset.typeChip === type;
        chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) activeLabel = chip.textContent.trim();
      });
      const lineGroup = qs('[data-line-group]', gridWrap);
      if (lineGroup) lineGroup.hidden = true;
      const typeChips = qs('[data-type-chips]', gridWrap);
      if (typeChips) typeChips.hidden = true;
      if (breadcrumbExtra) {
        const labelEl = qs('[data-breadcrumb-extra-label]', breadcrumbExtra);
        if (labelEl) labelEl.textContent = activeLabel;
        breadcrumbExtra.hidden = false;
      }
    }
    return { init };
  })();

  /* ------------------------------------------------------------ Line filter
     Client-side Men/Women/Kids filtering for the "All" collection only —
     clicking a Line chip there filters the grid in place via ?line= instead
     of navigating away to a separate collection page. The chip group itself
     only ever renders on the All page (see main-collection-product-grid.liquid). */
  const LineFilter = (() => {
    function init() {
      const chipsWrap = qs('[data-line-chips]');
      if (!chipsWrap) return;
      const cards = qsa('[data-grid-card]');
      const chips = qsa('[data-line-chip]', chipsWrap);
      const line = new URLSearchParams(location.search).get('line') || '';
      let count = 0;
      cards.forEach((card) => {
        const match = !line || card.dataset.productLine === line;
        card.style.display = match ? '' : 'none';
        if (match) count++;
      });
      chips.forEach((chip) => chip.setAttribute('aria-pressed', chip.dataset.lineChip === line ? 'true' : 'false'));
      const countEl = qs('[data-filter-count]');
      if (countEl) countEl.textContent = count + (count === 1 ? ' style' : ' styles');
    }
    return { init };
  })();

  /* ------------------------------------------------------ Product gallery
     Full-image carousel for the PDP — arrows, dots, thumbnails, swipe and
     arrow-key navigation, driven purely by image index (works regardless of
     whether the product has a Colour option, unlike the old per-colour
     photo map which only ever showed one image on single-colour products). */
  const ProductGallery = (() => {
    function init() {
      qsa('[data-gallery]').forEach(setup);
    }
    function setup(root) {
      const photos = qsa('.pace-product__photo', root);
      if (photos.length < 2) return;
      const col = root.closest('.pace-product__media-col') || doc;
      const dots = qsa('[data-dot-index]', root);
      const thumbs = qsa('[data-thumb-index]', col);
      let index = 0;
      function goTo(i) {
        index = (i + photos.length) % photos.length;
        photos.forEach((p, j) => p.classList.toggle('is-active', j === index));
        dots.forEach((d, j) => d.classList.toggle('is-active', j === index));
        thumbs.forEach((t, j) => t.classList.toggle('is-active', j === index));
      }
      dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.dotIndex))));
      thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.thumbIndex))));
      let startX = 0, dx = 0, dragging = false;
      root.addEventListener('touchstart', (e) => { dragging = true; startX = e.touches[0].clientX; dx = 0; }, { passive: true });
      root.addEventListener('touchmove', (e) => { if (dragging) dx = e.touches[0].clientX - startX; }, { passive: true });
      root.addEventListener('touchend', () => {
        if (!dragging) return;
        dragging = false;
        if (Math.abs(dx) > 40) goTo(index + (dx < 0 ? 1 : -1));
      });
      root.setAttribute('tabindex', '0');
      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') goTo(index - 1);
        if (e.key === 'ArrowRight') goTo(index + 1);
      });
    }
    return { init };
  })();

  /* ------------------------------------------------------- Grid density
     Mobile-only 1-col / 2-col toggle for the collection grid. Preference is
     remembered per-device via localStorage (a per-viewer convenience, not
     shared state, so localStorage is the right tool here). */
  const GridToggle = (() => {
    const KEY = 'pace-grid-cols';
    function init() {
      qsa('[data-grid-toggle]').forEach(setup);
    }
    function setup(toggle) {
      const productGrid = qs('[data-product-grid]');
      if (!productGrid) return;
      const btns = qsa('[data-grid-cols]', toggle);
      let saved;
      try { saved = localStorage.getItem(KEY); } catch (e) { saved = null; }
      apply(saved === '2' ? '2' : '1');
      btns.forEach((btn) => btn.addEventListener('click', () => {
        apply(btn.dataset.gridCols);
        try { localStorage.setItem(KEY, btn.dataset.gridCols); } catch (e) {}
      }));
      function apply(cols) {
        productGrid.classList.toggle('is-compact', cols === '2');
        btns.forEach((btn) => btn.setAttribute('aria-pressed', btn.dataset.gridCols === cols ? 'true' : 'false'));
      }
    }
    return { init };
  })();

  /* --------------------------------------------------------- Card gallery
     Collection-grid card image carousel — cycles through a product's photos
     on hover (desktop) or swipe (touch), with clickable dots. Dot clicks
     stop propagation so they don't trigger the card's enclosing link. */
  const CardGallery = (() => {
    function init() {
      qsa('[data-card-gallery]').forEach(setup);
    }
    function setup(root) {
      const imgs = qsa('.pace-card__img', root);
      if (imgs.length < 2) return;
      const dots = qsa('[data-card-dot-index]', root);
      let index = 0, timer = null;
      function goTo(i) {
        index = (i + imgs.length) % imgs.length;
        imgs.forEach((img, j) => img.classList.toggle('is-active', j === index));
        dots.forEach((d, j) => d.classList.toggle('is-active', j === index));
      }
      function startCycle() {
        stopCycle();
        timer = setInterval(() => goTo(index + 1), 900);
      }
      function stopCycle() {
        if (timer) clearInterval(timer);
        timer = null;
      }
      root.addEventListener('mouseenter', startCycle);
      root.addEventListener('mouseleave', () => { stopCycle(); goTo(0); });
      dots.forEach((d) => {
        d.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          stopCycle();
          goTo(Number(d.dataset.cardDotIndex));
        });
      });
      let startX = 0, dx = 0, dragging = false;
      root.addEventListener('touchstart', (e) => { dragging = true; startX = e.touches[0].clientX; dx = 0; }, { passive: true });
      root.addEventListener('touchmove', (e) => { if (dragging) dx = e.touches[0].clientX - startX; }, { passive: true });
      root.addEventListener('touchend', () => {
        if (!dragging) return;
        dragging = false;
        if (Math.abs(dx) > 30) goTo(index + (dx < 0 ? 1 : -1));
      });
    }
    return { init };
  })();

  /* -------------------------------------------------------- Accordions */
  const Accordion = (() => {
    function init() {
      qsa('.pace-acc__toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
          const acc = btn.closest('.pace-acc');
          const open = acc.dataset.open === 'true';
          acc.dataset.open = String(!open);
          btn.setAttribute('aria-expanded', String(!open));
        });
      });
    }
    return { init };
  })();

  /* ----------------------------------------------------- Product picker */
  const ProductPicker = (() => {
    function init() {
      const root = qs('[data-product-root]');
      if (!root) return;
      let variants = [];
      try { variants = JSON.parse(root.dataset.productVariants || '[]'); } catch (e) { variants = []; }
      const colorKey = 'option' + (root.dataset.colorPosition || '1');
      const sizeKey = 'option' + (root.dataset.sizePosition || '2');

      const colorInputs = qsa('[data-option-color]', root);
      const sizeInputs = qsa('[data-option-size]', root);
      const addBtn = qs('[data-add-to-cart]', root);
      const errEl = qs('[data-size-error]', root);
      const priceEls = qsa('[data-product-price]', root);
      const compareEl = qs('[data-product-compare]', root);
      const form = qs('form[data-product-form]', root);

      function currentVariant() {
        const color = colorInputs.find((i) => i.checked);
        const size = sizeInputs.find((i) => i.checked);
        return variants.find((v) => (!color || v[colorKey] === color.value) && (!size || v[sizeKey] === size.value));
      }

      function refresh() {
        const v = currentVariant();
        if (v) priceEls.forEach((el) => { el.textContent = formatMoney(v.price); });
        if (v && compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) { compareEl.textContent = formatMoney(v.compare_at_price); compareEl.hidden = false; }
          else compareEl.hidden = true;
        }
        if (addBtn) addBtn.disabled = !v || !v.available;
      }

      colorInputs.forEach((i) => i.addEventListener('change', refresh));
      sizeInputs.forEach((i) => i.addEventListener('change', () => { if (errEl) errEl.style.opacity = '0'; refresh(); }));

      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const v = currentVariant();
          if (!v) { if (errEl) errEl.style.opacity = '1'; return; }
          Cart.add(v.id, 1, () => {
            const color = colorInputs.find((i) => i.checked);
            const size = sizeInputs.find((i) => i.checked);
            Toast.show(`Added to bag — ${root.dataset.productTitle} · ${color ? color.value : ''} · EU ${size ? size.value : ''}`);
          });
        });
      }

      refresh();
    }
    return { init };
  })();

  /* ------------------------------------------------------------- Cart */
  const Cart = (() => {
    let drawer, itemsEl, subtotalEl, countEls, badgeEls;
    function init() {
      drawer = qs('#pace-cart-drawer');
      itemsEl = qs('[data-cart-items]', drawer);
      subtotalEl = qs('[data-cart-subtotal]', drawer);
      countEls = qsa('[data-bag-count]');
      badgeEls = qsa('[data-bag-badge]');
      qsa('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
      qsa('[data-cart-close]').forEach((b) => b.addEventListener('click', close));
      const scrim = qs('.pace-cart__scrim', drawer);
      if (scrim) scrim.addEventListener('click', close);
      refresh();
    }
    function open() { if (drawer && !drawer.classList.contains('is-open')) { drawer.classList.add('is-open'); ScrollLock.lock(); } render(); }
    function close() { if (drawer && drawer.classList.contains('is-open')) { drawer.classList.remove('is-open'); ScrollLock.unlock(); } }
    function add(variantId, quantity, cb) {
      fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      }).then((r) => r.json()).then(() => { refresh(() => { open(); if (cb) cb(); }); })
        .catch(() => Toast.show('Could not add to bag — try again'));
    }
    function changeLine(line, quantity) {
      fetch('/cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      }).then((r) => r.json()).then((cart) => { renderFromCart(cart); updateCount(cart.item_count); });
    }
    function refresh(cb) {
      fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json()).then((cart) => {
        updateCount(cart.item_count);
        renderFromCart(cart);
        if (cb) cb();
      });
    }
    function updateCount(count) {
      countEls.forEach((el) => { el.textContent = String(count); });
      badgeEls.forEach((el) => el.classList.toggle('is-visible', count > 0));
    }
    function render() { fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json()).then(renderFromCart); }
    function renderFromCart(cart) {
      if (!itemsEl) return;
      if (!cart.items.length) {
        itemsEl.innerHTML = '<div class="pace-cart__empty">Your bag is empty</div>';
      } else {
        itemsEl.innerHTML = cart.items.map((item, i) => `
          <div class="pace-cart__line">
            <img src="${item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : ''}" alt="" loading="lazy">
            <div>
              <div class="pace-cart__line-title">${item.product_title}</div>
              <div class="pace-cart__line-meta">${item.variant_title || ''}</div>
              <div class="pace-cart__line-meta" data-cart-qty>
                <button type="button" data-qty-minus data-line="${i + 1}" aria-label="Decrease quantity">&minus;</button>
                <span>${item.quantity}</span>
                <button type="button" data-qty-plus data-line="${i + 1}" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="pace-cart__line-price">${formatMoney(item.final_line_price)}</div>
          </div>`).join('');
        qsa('[data-qty-minus]', itemsEl).forEach((b) => b.addEventListener('click', () => {
          const line = Number(b.dataset.line);
          const item = cart.items[line - 1];
          changeLine(line, Math.max(0, item.quantity - 1));
        }));
        qsa('[data-qty-plus]', itemsEl).forEach((b) => b.addEventListener('click', () => {
          const line = Number(b.dataset.line);
          const item = cart.items[line - 1];
          changeLine(line, item.quantity + 1);
        }));
      }
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }
    return { init, add, refresh };
  })();

  /* -------------------------------------------------- Predictive search */
  const Search = (() => {
    let root, input, resultsEl, emptyEl, noneEl, countEl, debounceT;
    function init() {
      root = qs('#pace-search');
      if (!root) return;
      input = qs('#pace-search-input', root);
      resultsEl = qs('[data-search-results]', root);
      emptyEl = qs('[data-search-empty]', root);
      noneEl = qs('[data-search-none]', root);
      countEl = qs('[data-search-count]', root);
      qsa('[data-search-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); openSearch(); }));
      qsa('[data-search-close]').forEach((b) => b.addEventListener('click', closeSearch));
      qsa('[data-search-chip]', root).forEach((b) => b.addEventListener('click', () => { input.value = b.dataset.searchChip; runQuery(input.value); }));
      doc.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeSearch();
          const menu = qs('#pace-menu');
          if (menu && !menu.hidden) { menu.hidden = true; ScrollLock.unlock(); }
        }
      });
      if (input) input.addEventListener('input', () => { clearTimeout(debounceT); debounceT = setTimeout(() => runQuery(input.value), 220); });
    }
    function openSearch() {
      if (root.classList.contains('is-open')) return;
      root.classList.add('is-open');
      const menu = qs('#pace-menu');
      if (menu && !menu.hidden) { menu.hidden = true; ScrollLock.unlock(); }
      ScrollLock.lock();
      setTimeout(() => input && input.focus(), 50);
    }
    function closeSearch() { if (root.classList.contains('is-open')) { root.classList.remove('is-open'); ScrollLock.unlock(); } }
    const STOP_WORDS = ['the', 'a', 'an', 'for', 'and', 'or', 'in', 'on', 'of', 'with'];
    function suggest(term) {
      const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
      return fetch(url, { credentials: 'same-origin' }).then((r) => r.json()).then((data) => (data.resources && data.resources.results && data.resources.results.products) || []);
    }
    function runQuery(raw) {
      const q = raw.trim();
      if (!q) { showState('empty'); return; }
      const underMatch = q.match(/under\s*(?:rs\.?\s*)?([\d,]+)/i);
      const searchTerm = underMatch ? q.replace(underMatch[0], '').trim() || q.replace(underMatch[0], '') : q;
      suggest(searchTerm || q).then((products) => {
        if (underMatch) {
          const ceiling = parseInt(underMatch[1].replace(/,/g, ''), 10) * 100;
          products = products.filter((p) => (p.price_min != null ? Math.round(Number(p.price_min) * 100) : moneyToCents(p.price)) < ceiling);
        }
        if (products.length || underMatch) { renderResults(products, q); return; }
        // No exact match — broaden to the single most distinctive word before giving up.
        const words = searchTerm.split(/\s+/).filter((w) => w.length > 2 && !STOP_WORDS.includes(w.toLowerCase()));
        const broadTerm = words.sort((a, b) => b.length - a.length)[0];
        if (broadTerm && broadTerm.toLowerCase() !== searchTerm.toLowerCase()) {
          suggest(broadTerm).then((broader) => renderResults(broader, q));
        } else {
          renderResults([], q);
        }
      }).catch(() => renderResults([], q));
    }
    function moneyToCents(str) { return (parseInt(String(str).replace(/[^0-9]/g, ''), 10) || 0) * 100; }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
    function renderResults(products, q) {
      if (!products.length) {
        showState('none');
        if (noneEl) noneEl.textContent = `No matches for "${q}". Try a line, a colour or a price.`;
        return;
      }
      showState('results');
      countEl.textContent = `${products.length} result${products.length === 1 ? '' : 's'}`;
      resultsEl.innerHTML = products.map((p) => `
        <a class="pace-search__row" href="${esc(p.url)}">
          <img src="${esc(p.image)}" alt="">
          <span style="font-size:15px;font-weight:500">${esc(p.title)}</span>
          <span class="pace-search__row-price">${esc(formatMoney(p.price_min != null ? Math.round(Number(p.price_min) * 100) : moneyToCents(p.price)))}</span>
        </a>`).join('');
    }
    function showState(state) {
      if (emptyEl) emptyEl.hidden = state !== 'empty';
      if (resultsEl) resultsEl.parentElement.hidden = state !== 'results';
      if (noneEl) noneEl.hidden = state !== 'none';
    }
    return { init };
  })();

  /* ---------------------------------------------------------------- Init */
  const Pace = {
    init() {
      Toast.init(); Cursor.init(); Header.init(); Menu.init(); Curtain.init(); AnnouncementRotator.init();
      Search.init(); Cart.init();
      this.reinit();
      ScrollRig.init();
    },
    reinit() {
      Hero.init(); StoryChapters.init(); Accordion.init(); ProductPicker.init(); ProductGallery.init(); CardGallery.init(); CategoryGate.init(); LineFilter.init(); GridToggle.init(); GridReveal.init(); WheelScroll.init(); SizeGuide.init();
      ScrollRig.initGallerySizing();
      requestAnimationFrame(() => { Header.update(); });
    },
  };
  window.Pace = Pace;

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => Pace.init());
  else Pace.init();

  /* Theme editor swaps a section's markup in place (settings/CSS edits, block
     reorder) without a full page reload, so event bindings tied to the old
     nodes (like WheelScroll's listeners) go stale until we reinit against
     the fresh ones. */
  doc.addEventListener('shopify:section:load', () => { Pace.reinit(); ScrollRig.schedule(); });
  doc.addEventListener('shopify:section:select', () => ScrollRig.schedule());
  doc.addEventListener('shopify:block:select', () => ScrollRig.schedule());
})();
